import {
  CodedError,
  apiErrorResponseSchema,
  isErrorCode,
  publicBroadcasterListSchema,
  publicCatalogueSchema,
  publicMissionListSchema,
  publicMissionSchema,
  type ClientErrorReport,
  type Locale,
  type PublicBroadcaster,
  type PublicCatalogue,
  type PublicMission,
} from '@ebu/contracts';
import type { z } from 'zod';

/**
 * Narrow typed adapter for the same-origin API. Every response is validated against the shared
 * contract; network or contract failures become WEB-API-001, server errors keep their own code.
 */
export async function apiGet<T>(
  path: string,
  schema: z.ZodType<T>,
  options: { fetchImpl?: typeof fetch; allowErrorStatus?: boolean; signal?: AbortSignal } = {},
): Promise<T> {
  const fetchImpl = options.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(path, {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // A superseded request (e.g. broadcaster switched again) is not an error; let callers ignore it.
    if (options.signal?.aborted) throw cause;
    throw new CodedError('WEB-API-001', `Network request failed: ${path}`, {
      cause,
      context: { path },
    });
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (cause) {
    throw new CodedError('WEB-API-001', `Non-JSON response: ${path}`, {
      cause,
      context: { path, status: response.status },
    });
  }

  if (!response.ok && !options.allowErrorStatus) {
    const parsedError = apiErrorResponseSchema.safeParse(json);
    const code =
      parsedError.success && isErrorCode(parsedError.data.error.code)
        ? parsedError.data.error.code
        : 'WEB-API-001';
    throw new CodedError(code, `API ${response.status} for ${path}`, {
      httpStatus: response.status,
      context: {
        path,
        status: response.status,
        requestId: parsedError.success ? parsedError.data.error.requestId : undefined,
      },
    });
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new CodedError('WEB-API-001', `Response failed contract validation: ${path}`, {
      context: { path, issues: parsed.error.issues.map((i) => i.path.join('.')) },
    });
  }
  return parsed.data;
}

/** Public broadcaster configurations for the guest selector (no sign-in, no cookies). */
export async function fetchBroadcasters(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<PublicBroadcaster[]> {
  const list = await apiGet('/api/public/broadcasters', publicBroadcasterListSchema, options);
  return list.broadcasters;
}

/** Rights-filtered, curated catalogue for one broadcaster and requested locale. */
export async function fetchCatalogue(
  broadcasterId: string,
  locale: Locale,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<PublicCatalogue> {
  const query = new URLSearchParams({ broadcaster: broadcasterId, locale });
  return apiGet(`/api/public/catalogue?${query.toString()}`, publicCatalogueSchema, options);
}

/** Published missions for the Lab, localised. */
export async function fetchMissions(
  locale: Locale,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<PublicMission[]> {
  const query = new URLSearchParams({ locale });
  const list = await apiGet(
    `/api/public/missions?${query.toString()}`,
    publicMissionListSchema,
    options,
  );
  return list.missions;
}

/** One published mission; an unknown or unpublished id is GAME-MISSION-001 (404). */
export async function fetchMission(
  id: string,
  locale: Locale,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<PublicMission> {
  const query = new URLSearchParams({ locale });
  return apiGet(
    `/api/public/missions/${encodeURIComponent(id)}?${query.toString()}`,
    publicMissionSchema,
    options,
  );
}

/** Fire-and-forget delivery of a sanitised error report; `keepalive` survives page unload. */
export async function sendClientErrorReport(
  report: ClientErrorReport,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl('/api/diagnostics/client-errors', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(report),
    keepalive: true,
    credentials: 'omit',
  });
  if (!response.ok) throw new Error(`Error report rejected with HTTP ${response.status}`);
}
